import type { Patrol, Person } from '@local/common-lib';
import {
  LoadingButton,
  LocalizationProvider,
  MobileDateTimePicker,
} from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon/index.js';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { FormikProps } from 'formik';
import { DateTime } from 'luxon';
import type { FC } from 'react';

export interface AdminCompletePatrolData {
  finished: Date;
  guestsPresent: string[];
  notes: string;
  patrollersPresent: string[];
  started: Date;
}

interface Props {
  formik: FormikProps<AdminCompletePatrolData>;
  onClose: () => void;
  open: boolean;
  patrol: Patrol.Type;
  persons: Person.View[];
}

const toggle = (values: string[], id: string): string[] =>
  values.includes(id)
    ? values.filter((value) => value !== id)
    : [...values, id];

export const AdminCompletePatrolDialog: FC<Props> = ({
  formik,
  onClose,
  open,
  patrol,
  persons,
}) => {
  const assignments = patrol.patrollers ?? {};
  const assignedPatrollers = Object.keys(assignments)
    .filter(
      (personId) =>
        !assignments[personId].declined && !assignments[personId].removed,
    )
    .map((personId) => persons.find((person) => person._id === personId))
    .filter((person): person is Person.View => !!person);
  const guests = Object.entries(patrol.guests ?? {}).filter(
    ([, guest]) => !guest.removed,
  );

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
      sx={{ zIndex: 10001 }}
    >
      <form onReset={formik.handleReset} onSubmit={formik.handleSubmit}>
        <DialogTitle>Mark Patrol as Completed</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Use this to record a patrol that went ahead but was not started or
            finished in the app. The patrol will be recorded as completed
            administratively and the summary email will be sent.
          </DialogContentText>

          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Stack direction="column" spacing={3}>
              {patrol.started ? (
                <Alert severity="info">
                  This patrol was already started at{' '}
                  {DateTime.fromJSDate(patrol.started.started).toLocaleString(
                    DateTime.DATETIME_SHORT,
                  )}
                  . The existing start details will be kept.
                </Alert>
              ) : (
                <MobileDateTimePicker
                  disabled={formik.isSubmitting || !!patrol.started}
                  label="Start time"
                  maxDateTime={DateTime.now()}
                  onChange={(newValue: DateTime | null) => {
                    if (newValue) {
                      formik.setFieldValue('started', newValue.toJSDate());
                    }
                  }}
                  renderInput={(params: object) => (
                    <TextField {...params} fullWidth />
                  )}
                  value={DateTime.fromJSDate(formik.values.started)}
                />
              )}

              <MobileDateTimePicker
                disabled={formik.isSubmitting}
                label="Finish time"
                maxDateTime={DateTime.now()}
                onChange={(newValue: DateTime | null) => {
                  if (newValue) {
                    formik.setFieldValue('finished', newValue.toJSDate());
                  }
                }}
                renderInput={(params: object) => (
                  <TextField {...params} fullWidth />
                )}
                value={DateTime.fromJSDate(formik.values.finished)}
              />

              <div>
                <Typography variant="subtitle2">Patrollers present</Typography>
                <FormGroup>
                  {assignedPatrollers.map((person) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.patrollersPresent.includes(
                            person._id,
                          )}
                          onChange={() =>
                            formik.setFieldValue(
                              'patrollersPresent',
                              toggle(
                                formik.values.patrollersPresent,
                                person._id,
                              ),
                            )
                          }
                        />
                      }
                      key={person._id}
                      label={`${person.firstName} ${person.lastName.toLocaleUpperCase()}`}
                    />
                  ))}
                </FormGroup>
              </div>

              {guests.length ? (
                <div>
                  <Typography variant="subtitle2">Guests present</Typography>
                  <FormGroup>
                    {guests.map(([guestId, guest]) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.guestsPresent.includes(
                              guestId,
                            )}
                            onChange={() =>
                              formik.setFieldValue(
                                'guestsPresent',
                                toggle(formik.values.guestsPresent, guestId),
                              )
                            }
                          />
                        }
                        key={guestId}
                        label={guest.name}
                      />
                    ))}
                  </FormGroup>
                </div>
              ) : undefined}

              <TextField
                fullWidth
                id="notes"
                label="Notes"
                multiline
                name="notes"
                onChange={formik.handleChange}
                rows={2}
                value={formik.values.notes}
              />
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button disabled={formik.isSubmitting} type="reset">
            Cancel
          </Button>
          <LoadingButton
            loading={formik.isSubmitting}
            type="submit"
            variant="contained"
          >
            Mark Completed
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
